import React, { useState, useEffect } from "react";
import {
  getDailyStatus,
  syncDailyPlan,
  lockDay,
  unlockDay,
  getDoneRows,
  toggleRowDone,
  regenerateDay,
  getDisruptionsByDate,
  toggleSubtaskDone,
  applyDayDisruption,
  applyWeekDisruption,
  swapAcrossDays,
  undoSwapAcrossDays,
  getSwapsForDay,
} from "../api";
import { C, TABLE, DAY_THEMES, DAY_NAMES } from "../constants";
import { LAYOUT } from "../constants/colors";
import { toDD, parseDayKey, getDateForWeekDayFE, getWeekMeta } from "../utils";
import { RichLineText } from "./richPlanText";
import { parsePlanTaskLines as parseTaskLines } from "../utils/planTaskLines";
import { displayRowContent } from "../utils/sanitizeLearningContent";
import { buildVideoPlan, videoKey, withLearningResources } from "../utils/learningResourceLinks";
import { resolveDtStep } from "../constants/dtPlaybook";
import LearningResourceChips from "./LearningResourceChips";
import { buildWeekGrid, dayGridLabel, formatClock, restSlotPlain } from "../utils/weekScheduleGrid";
import { cellsForWeekDay, origIndexForCell } from "../utils/weekDaySync";
import { buildTonightHomework, isHomeworkActivity, topicsFromDayRows } from "../utils/homeworkRows";
import { connectDayCell, extractProblemFromRows } from "../utils/scheduleConnect";
import { applyOverridesToWeekGrid, displaySlotContent, resolveSlotOverride } from "../utils/slotOverrides";
import { iconForSlotFamily, SLOT_OVERRIDE_KEY, splitStoredOverrides, visibleDailyBlueprint } from "../constants/dayActivities";
import TableColumnsMenu from "./TableColumnsMenu";
import { useTablePersonalize } from "../hooks/useTablePersonalize";

const DAILY_LIST_COLS = [
  { id: "check", label: "Done", icon: "ti-square-check", width: "6%" },
  { id: "time", label: "Time", icon: "ti-clock", width: "12%" },
  { id: "activity", label: "Activity", icon: "ti-category", width: "21%" },
  { id: "content", label: "Task / Content", icon: "ti-align-left", width: "61%" },
];

function readSlotOverrides() {
  try {
    return splitStoredOverrides(JSON.parse(localStorage.getItem(SLOT_OVERRIDE_KEY) || "{}")).daily;
  } catch {
    return {};
  }
}

/** Neat Activity labels for display (matches backend enricher). */
function neatActivityTitle(raw) {
  const a = String(raw || "").toLowerCase();
  if (/standup|stand[\s-]?up|kickoff|kick[\s-]?off|opening|morning brief/.test(a)) return "Stand-Up";
  if (/lunch/.test(a)) return "Lunch";
  if (/refresh|hollywoodlywood|bollywood|pictionary|hot seat|emoji empathy|stack shuffle|two truths|pitch relay|🎮/.test(a)) return "Refresh Game";
  if (/problem review|problem statement review/.test(a)) return "Problem Review";
  if (/product speak|product of the day|🗣️/.test(a)) return "Speak & Solve";
  if (/placement|dream company|target company|🎯/.test(a)) return "Placement Prep";
  if (/retro|retrospective|reflect/.test(a)) return "Retrospective";
  if (/mini build|interest build|side build/.test(a)) return "Mini Build";
  if (/\bokr\b|okrion/.test(a)) return "OKRion";
  if (/peer review|board update|capability|interests?|goals?|domain/.test(a)) return "Mini Build";
  if (/agent workbench|ai agent builder|sns agent|🤖/.test(a)) return "AI Agent Builder";
  if (/3c|speak\s*&\s*solve|🎤|aptitude/.test(a)) return "Speak & Solve";
  if (/dsa|leetcode|coding practice|🧮|problem solving/.test(a)) return "Coding Practice";
  if (/system design|architecture|🏗️/.test(a)) return "System Design";
  if (/project micro|project push|micro-push/.test(a)) return "Project Push";
  if (/dt playbook|project build|implement|💻|prototype|wireframe|project/.test(a)) return "Project Build";
  if (/learning|📖|📚|concept|subject/.test(a)) return "Learning";
  if (/short break|break|☕|🧘|hydrate/.test(a)) return "Break";
  if (/group|workshop|discussion/.test(a)) return "Group Work";
  return String(raw || "")
    .replace(/^[\s🚀🔍🧪📚🔧🎯🗣️🎮🔄💻📖🧮🏗️🛠️☕🧘🍽️🎤🤖⚡★☆•\-–—]+/u, "")
    .replace(/\s*[—–-]\s*.+$/, "")
    .trim() || "Activity";
}

const getRowStyle = (activity = "") => {
  const text = neatActivityTitle(activity).toLowerCase();
  if (text.includes("learning")) return { bg: C.primarySoft, accent: C.primary, dot: C.primary };
  if (text.includes("project")) return { bg: C.successBg, accent: C.success, dot: C.success };
  if (text.includes("coding") || text.includes("dsa")) return { bg: C.warningBg, accent: C.primaryDark, dot: C.primary };
  if (text.includes("placement")) return { bg: C.primarySoft, accent: C.primaryDark, dot: C.primary };
  if (text.includes("product") || text.includes("speak")) return { bg: C.primarySoft, accent: C.primary, dot: C.primary };
  if (text.includes("refresh") || text.includes("game")) return { bg: C.successBg, accent: C.success, dot: C.success };
  if (text.includes("retro")) return { bg: C.successBg, accent: C.success, dot: C.success };
  if (text.includes("break")) return { bg: "#F3F4F6", accent: "#6B7280", dot: "#6B7280" };
  if (text.includes("lunch")) return { bg: C.primarySoft, accent: C.primary, dot: C.primary };
  if (text.includes("problem lab") || text.includes("problem review")) return { bg: C.primarySoft, accent: C.primaryDark, dot: C.primaryDark };
  if (text.includes("stand")) return { bg: C.successBg, accent: C.success, dot: C.success };
  if (text.includes("system") || text.includes("framing")) return { bg: C.successBg, accent: C.success, dot: C.success };
  if (text.includes("okrion") || text.includes("okr")) return { bg: C.primarySoft, accent: C.primaryDark, dot: C.primary };
  if (text.includes("agent")) return { bg: C.successBg, accent: C.success, dot: C.success };
  if (text.includes("peer")) return { bg: C.successBg, accent: C.success, dot: C.success };
  return { bg: "#FFFFFF", accent: C.text, dot: C.faint };
};

export default function DailyPlanTable({
  plan, calMap, calendarMeta, onDayRegenerated, editMode = false, onPlanChange,
  focusWeek = null, focusDay = null, focusToken = null,
  memberMeta = null,
  scheduleLayout: layoutProp,
  onScheduleLayoutChange,
}) {
  const [activeWeek, setActiveWeek]       = useState(1);
  const [activeDay, setActiveDay]         = useState("Monday");
  const [doneRows, setDoneRows]           = useState(new Set());
  const [subTaskDone, setSubTaskDone]     = useState(new Set()); // keys: "rowIndex-subIndex"
  const [regenOpen, setRegenOpen]         = useState(false);
  const [regenFeedback, setRegenFeedback] = useState("");
  const [regenLoading, setRegenLoading]   = useState(false);
  const [regenMsg, setRegenMsg]           = useState(null);
  const [editDraft, setEditDraft]         = useState(null); // { ri, activity, content }
  // Edit toggle lives only on ModuleCard / TeamMemberPlanCard (single place)
  const editing = Boolean(editMode);

  // Disruption state
  const [disruptOpen, setDisruptOpen]       = useState(false);
  const [selectedSlots, setSelectedSlots]   = useState([]);
  const [disruptMode, setDisruptMode]       = useState("swap");
  const [manualTasks, setManualTasks]       = useState({});
  const [disruptReason, setDisruptReason]   = useState("");
  const [disruptLoading, setDisruptLoading] = useState(false);
  const [disruptMsg, setDisruptMsg]         = useState(null);
  const [disruptionsList, setDisruptionsList] = useState([]);
  const [undoLoading, setUndoLoading]       = useState(false);
  const [undoVersion, setUndoVersion]       = useState(null);
  const [visualRows, setVisualRows]         = useState(null); 
  const [disruptScope, setDisruptScope]       = useState("slot");
  const [wholeAltType, setWholeAltType]       = useState("self_study");
  const [wholeReason, setWholeReason]         = useState("");
  const [wholeCustomTitle, setWholeCustomTitle] = useState("");
  const [wholeLoading, setWholeLoading]       = useState(false);
  const [wholeMsg, setWholeMsg]               = useState(null);

  const [crossWeekB, setCrossWeekB]         = useState(1);
  const [crossDayB, setCrossDayB]           = useState("Monday");
  const [crossMode, setCrossMode]           = useState("slots"); // "slots" | "whole"
  const [crossSlotPairs, setCrossSlotPairs] = useState([]);      // [{timeA, timeB}]
  const [pendingA, setPendingA]             = useState(null);    // time selected in column A, awaiting its column-B partner
  const [crossMsg, setCrossMsg]             = useState(null);
  const [crossLoading, setCrossLoading]     = useState(false);
  const [lastSwapId, setLastSwapId]         = useState(null);
  const [swapHistory, setSwapHistory]       = useState([]);

  // Lock state
  const [lockStatus, setLockStatus] = useState({});
  const [lockMsg, setLockMsg]       = useState(null);
  const [lockLoading, setLockLoading] = useState(false);
  const [synced, setSynced]         = useState(false);
  const [internalLayout, setInternalLayout] = useState("weekGrid");
  const scheduleLayout = layoutProp || internalLayout;
  const setScheduleLayout = (next) => {
    if (onScheduleLayoutChange) onScheduleLayoutChange(next);
    if (layoutProp == null) setInternalLayout(next);
  };
  const [selectedGrid, setSelectedGrid] = useState(null); // { dayName, time }
  const listCols = useTablePersonalize("daily-list");
  const gridCols = useTablePersonalize("daily-grid");

  useEffect(() => {
    if (editing) return;
    setEditDraft(null);
  }, [editing]);

  // Load lock status on mount
  useEffect(() => {
    getDailyStatus().then((r) => {
      if (r.success) {
        const map = {};
        (r.days || []).forEach((d) => {
          map[d.dayId] = { locked: d.locked, completed: d.completed };
        });
        setLockStatus(map);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (focusWeek == null && !focusDay) return;
    if (focusWeek != null && Number.isFinite(Number(focusWeek))) {
      setActiveWeek(Number(focusWeek));
    }
    if (focusDay) {
      const match = DAY_NAMES.find(
        (d) => String(d).toLowerCase() === String(focusDay).toLowerCase()
      );
      if (match) setActiveDay(match);
    }
  }, [focusWeek, focusDay, focusToken]);

  // ── Build week/day index from plan rows ───────────────────────────────────
  const byWeekDay = {};
  const rawDayKeyByWeekDay = {};
  let autoWeek = 1;
  const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  let lastDayIdx = -1;

  (plan.rows || []).forEach((row) => {
    const parsed = parseDayKey(row[0]);
    if (!parsed) return;
    let { week, dayName } = parsed;
    if (week === null) {
      const dIdx = DAY_ORDER.indexOf(dayName);
      if (dIdx !== -1 && dIdx <= lastDayIdx) autoWeek++;
      lastDayIdx = dIdx !== -1 ? dIdx : lastDayIdx;
      week = autoWeek;
    }
    if (!byWeekDay[week]) byWeekDay[week] = {};
    if (!byWeekDay[week][dayName]) byWeekDay[week][dayName] = [];
    byWeekDay[week][dayName].push(row);
    if (!rawDayKeyByWeekDay[week]) rawDayKeyByWeekDay[week] = {};
    if (!rawDayKeyByWeekDay[week][dayName]) rawDayKeyByWeekDay[week][dayName] = row[0];
  });

  const weeks        = Object.keys(byWeekDay).map(Number).sort((a, b) => a - b);
  const plannedDayCount = Object.values(byWeekDay).reduce(
    (total, days) => total + Object.keys(days).length,
    0
  );
  const finalWeek = weeks[weeks.length - 1] || 1;
  const finalWeekDays = Object.keys(byWeekDay[finalWeek] || {});
  const finalDay = finalWeekDays[finalWeekDays.length - 1] || "Friday";
  const daysInWeek   = byWeekDay[activeWeek] || {};
  const dayRows      = daysInWeek[activeDay] || [];
  const activeDayKey = (rawDayKeyByWeekDay[activeWeek] || {})[activeDay] || null;
  const planProblem = extractProblemFromRows(plan.rows || [], plan.problem || plan.problemStatement || "");
  const videoPlan = React.useMemo(() => buildVideoPlan(plan.rows || []), [plan.rows]);
  const dayIndexByKey = React.useMemo(() => {
    const m = new Map();
    (plan.rows || []).forEach((r) => {
      const k = String(r?.[0] || "").trim();
      if (k && !m.has(k)) m.set(k, m.size);
    });
    return m;
  }, [plan.rows]);
  const connectCtxFor = (rows, dayName) => {
    const t = topicsFromDayRows(rows);
    const names = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
    const dayIdx = dayIndexByKey.get(String(rows?.[0]?.[0] || "").trim()) ?? null;
    const dayText = (rows || []).map((r) => String(r?.[3] || "")).join("\n");
    return {
      problem: extractProblemFromRows(rows, planProblem) || planProblem,
      topic: t.topic,
      build: t.build,
      dow: Math.max(0, names.indexOf(dayName)),
      dayIdx,
      dtStep: resolveDtStep(dayText, dayIdx),
    };
  };
  const theme        = DAY_THEMES[activeDay] || DAY_THEMES.Monday;
  const realDate     = calMap ? getDateForWeekDayFE(calMap, activeWeek, activeDay) : null;

  const { iaWeeks, auWeeks, mockWeek } = getWeekMeta(calendarMeta);

  const isIAWeek   = iaWeeks.has(activeWeek);
  const isAUWeek   = auWeeks.has(activeWeek);
  const isMockWeek = mockWeek ? activeWeek === mockWeek : false;

  const fridayLabel = isMockWeek ? "Mock Exam Day"
    : isAUWeek  ? "AU Exam Day"
    : isIAWeek  ? "Internal Assessment Day"
    : "Weekly Assessment Day";

  const gridDaysInWeek = { ...daysInWeek };
  if (visualRows && activeDay) gridDaysInWeek[activeDay] = visualRows;
  const slotOverrides = readSlotOverrides();
  const weekGridRaw = buildWeekGrid(gridDaysInWeek, visibleDailyBlueprint(slotOverrides));
  const weekGrid = applyOverridesToWeekGrid(weekGridRaw, slotOverrides);

  // Reset panel state when the viewed day changes
  useEffect(() => {
    setDoneRows(new Set());
    setRegenOpen(false);
    setRegenMsg(null);
    setDisruptOpen(false);
    setSelectedSlots([]);
    setManualTasks({});
    setDisruptReason("");
    setVisualRows(null);
    setDisruptMsg(null);
    if (!activeDayKey) return;
    getDoneRows(activeDayKey).then((r) => {
      if (r.success) setDoneRows(new Set(r.doneRows || []));
    }).catch(() => {});
  }, [activeDayKey]);

  // Load disruptions when viewed day changes
  const loadDisruptions = async () => {
    if (!activeDayKey) return;
    const m = activeDayKey.match(/\((\d{2})-(\d{2})-(\d{4})\)/);
    if (!m) return;
    const isoDate = `${m[3]}-${m[2]}-${m[1]}`;
    try {
      const res = await getDisruptionsByDate(isoDate);
      if (res.success) setDisruptionsList(res.disruptions || []);
    } catch (_) {}
  };

  useEffect(() => {
    if (activeDayKey) loadDisruptions();
  }, [activeDayKey]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSync = async (activeDayKey) => {
    if (!plan) return;
    setLockLoading(true); setLockMsg(null);
    try {
      const res = await syncDailyPlan(plan);
      if (res.success) {
        const r = await getDailyStatus();
        if (r.success) {
          const map = {};
          (r.days || []).forEach((d) => {
            map[d.dayId] = { locked: d.locked, completed: d.completed };
          });
          setLockStatus(map);
        }
        setSynced(true);
        setLockMsg({ type: "success", text: `✅ Synced ${res.daysSynced} days` });
        setTimeout(() => setLockMsg(null), 3000);
      }
    } catch (err) {
      setLockMsg({ type: "error", text: err.response?.data?.error || "Sync failed" });
    }
    setLockLoading(false);
  };

  const handleLockDay = async (dayId) => {
    setLockLoading(true); setLockMsg(null);
    try {
      if (!lockStatus[dayId]) await syncDailyPlan(plan).catch(() => {});
      const res = await lockDay(dayId);
      if (res.success) {
        setLockStatus((p) => ({ ...p, [dayId]: { locked: true, completed: true } }));
        setLockMsg({ type: "success", text: "🔒 Day locked — won't be touched by regeneration or leave engine" });
        setTimeout(() => setLockMsg(null), 4000);
      }
    } catch (err) {
      setLockMsg({ type: "error", text: err.response?.data?.error || "Lock failed" });
    }
    setLockLoading(false);
  };

  const handleUnlockDay = async (dayId) => {
    setLockLoading(true); setLockMsg(null);
    try {
      const res = await unlockDay(dayId);
      if (res.success) {
        setLockStatus((p) => ({ ...p, [dayId]: { locked: false, completed: false } }));
        setLockMsg({ type: "success", text: "🔓 Day unlocked — now available for regeneration" });
        setTimeout(() => setLockMsg(null), 3000);
      }
    } catch (err) {
      setLockMsg({ type: "error", text: err.response?.data?.error || "Unlock failed" });
    }
    setLockLoading(false);
  };

  const handleToggleDone = async (rowIndex) => {
    if (!activeDayKey) return;
    const willBeDone = !doneRows.has(rowIndex);
    const rowContent = (visualRows || dayRows)[rowIndex]?.[3];
    const trackable = parseTaskLines(rowContent).filter((l) => l.trackable);

    // Overall checkbox: strike / unstrike every task in this activity
    const prevDone = doneRows;
    const prevSub = subTaskDone;
    const nextDone = new Set(doneRows);
    if (willBeDone) nextDone.add(rowIndex); else nextDone.delete(rowIndex);
    const nextSub = new Set(subTaskDone);
    for (const l of trackable) {
      const key = `${rowIndex}-${l.subIndex}`;
      if (willBeDone) nextSub.add(key); else nextSub.delete(key);
    }
    setDoneRows(nextDone);
    setSubTaskDone(nextSub);

    try {
      await toggleRowDone(activeDayKey, rowIndex, willBeDone);
      // Persist each subtask so reload keeps the same struck state
      await Promise.all(
        trackable.map((l) =>
          toggleSubtaskDone(activeDayKey, rowIndex, l.subIndex, willBeDone).catch(() => null)
        )
      );
    } catch {
      setDoneRows(prevDone);
      setSubTaskDone(prevSub);
    }
  };
const handleToggleSubtask = async (rowIndex, subIndex) => {
    if (!activeDayKey) return;
    const key = `${rowIndex}-${subIndex}`;
    const next = new Set(subTaskDone);
    const willBeDone = !next.has(key);
    if (willBeDone) next.add(key); else next.delete(key);
    setSubTaskDone(next);
    try {
      await toggleSubtaskDone(activeDayKey, rowIndex, subIndex, willBeDone);
    } catch {
      setSubTaskDone(subTaskDone); // revert on failure
      return;
    }
const rowContent  = (visualRows || dayRows)[rowIndex]?.[3];
    const trackable   = parseTaskLines(rowContent).filter((l) => l.trackable);
    const allDone     = trackable.length > 0 && trackable.every((l) =>
      l.subIndex === subIndex ? willBeDone : next.has(`${rowIndex}-${l.subIndex}`)
    );
    const rowCurrentlyDone = doneRows.has(rowIndex);
    if (trackable.length > 0 && allDone !== rowCurrentlyDone) {
      const nextDone = new Set(doneRows);
      if (allDone) nextDone.add(rowIndex); else nextDone.delete(rowIndex);
      setDoneRows(nextDone);
      try {
        await toggleRowDone(activeDayKey, rowIndex, allDone);
      } catch {
        setDoneRows(doneRows); // revert row-level state on failure
      }
    }
  };

  const handleRegenerateDay = async () => {
    if (!activeDayKey) return;
    setRegenLoading(true); setRegenMsg(null);
    try {
      const learningRows = dayRows.filter((r) =>
        String(r[2] || "").toLowerCase().includes("learning")
      );
      const projectRow = dayRows.find((r) =>
        String(r[2] || "").toLowerCase().includes("project")
      );
      const dsaRow = dayRows.find((r) =>
        String(r[2] || "").toLowerCase().includes("dsa")
      );

      const subjectContext = [];
      if (learningRows.length > 0) {
        const content = String(learningRows[0][3] || "");
        const subjectMatches = content.match(/▶\s*([^:]+):\s*"([^"]+)"/g) || [];
        subjectMatches.forEach((m) => {
          const parts = m.match(/▶\s*([^:]+):\s*"([^"]+)"/);
          if (parts) subjectContext.push({ name: parts[1].trim(), topic: parts[2].trim() });
        });
      }

      const context = {
        feedback: regenFeedback,
        subjects: subjectContext.length > 0
          ? subjectContext
          : [{ name: activeDay + " subjects", topic: "continue current week's topics" }],
        theme: {
          dsa:          dsaRow     ? String(dsaRow[3]     || "").slice(0, 200) : "",
          project:      projectRow ? String(projectRow[3] || "").slice(0, 400) : "",
          systemDesign: "",
          tech:         "",
        },
      };

      const res = await regenerateDay(activeDayKey, context, dayRows, memberMeta || {});
      if (res.success) {
        setRegenMsg({ type: "success", text: "Day regenerated successfully." });
        onDayRegenerated && onDayRegenerated(activeDayKey, res.day.rows);
        setRegenFeedback("");
        setRegenOpen(false);
      } else {
        setRegenMsg({ type: "error", text: res.error || "Regeneration failed" });
      }
    } catch (err) {
      setRegenMsg({ type: "error", text: err.response?.data?.error || "Day is locked or regeneration failed" });
    }
    setRegenLoading(false);
  };

  const saveRowEdit = () => {
    if (!editDraft || !onPlanChange || !plan?.rows) return;
    const dayId = String(editDraft.dayId || "");
    const time = String(editDraft.time || "").trim();
    if (dayId) {
      const nextRows = plan.rows.map((r) => {
        if (String(r[0] || "") === dayId && String(r[1] || "").trim() === time) {
          return [r[0], r[1], editDraft.activity.trim() || r[2], editDraft.content];
        }
        return r;
      });
      onPlanChange({ ...plan, rows: nextRows });
      setVisualRows(null);
      setEditDraft(null);
      return;
    }
    const target = (visualRows || dayRows)[editDraft.ri];
    if (!target) return;
    const id = String(target[0] || "");
    const t = String(target[1] || "").trim();
    const nextRows = plan.rows.map((r) => {
      if (String(r[0] || "") === id && String(r[1] || "").trim() === t) {
        return [r[0], r[1], editDraft.activity.trim() || r[2], editDraft.content];
      }
      return r;
    });
    onPlanChange({ ...plan, rows: nextRows });
    setVisualRows(null);
    setEditDraft(null);
  };

  const toggleSlot = (timeVal) => {
    setSelectedSlots((prev) => {
      if (prev.includes(timeVal)) return prev.filter((t) => t !== timeVal);
      if (prev.length >= 2) return prev;
      return [...prev, timeVal];
    });
  };

  const handleDisrupt = () => {
    if (selectedSlots.length === 0) return;
    const baseRows = visualRows || dayRows;

    if (disruptMode === "swap" && selectedSlots.length === 2) {
      const [tA, tB] = selectedSlots;
      const idxA = baseRows.findIndex((r) => String(r[1] || "").trim() === tA);
      const idxB = baseRows.findIndex((r) => String(r[1] || "").trim() === tB);
      if (idxA === -1 || idxB === -1) {
        setDisruptMsg({ type: "error", text: "Could not locate both slots." });
        return;
      }
      const next = baseRows.map((r) => [...r]);
      [next[idxA][2], next[idxB][2]] = [next[idxB][2], next[idxA][2]];
      [next[idxA][3], next[idxB][3]] = [next[idxB][3], next[idxA][3]];
      setVisualRows(next);
      setDisruptMsg({ type: "success", text: `✅ Slots ${tA} ↔ ${tB} swapped (visual only — original plan unchanged).` });
    } else if (disruptMode === "manual") {
      const next = baseRows.map((r) => {
        const t = String(r[1] || "").trim();
        if (selectedSlots.includes(t) && manualTasks[t]?.trim()) {
          const label = disruptReason ? `⚡ ${disruptReason}` : "⚡ Manual Task";
          return [r[0], r[1], label, manualTasks[t].trim()];
        }
        return [...r];
      });
      setVisualRows(next);
      const filled = selectedSlots.filter((t) => manualTasks[t]?.trim()).length;
      setDisruptMsg({ type: "success", text: `✅ ${filled} slot(s) updated with manual task (visual only — original plan unchanged).` });
    } else {
      setDisruptMsg({
        type: "error",
        text: disruptMode === "swap" ? "Select exactly 2 slots to swap." : "Select at least 1 slot.",
      });
      return;
    }

    setSelectedSlots([]);
    setManualTasks({});
    setDisruptReason("");
    setUndoVersion({ reason: disruptMode === "swap" ? "Slot swap" : "Manual override" });
  };

  const handleRemoveDisruption = () => {
    setVisualRows(null);
    setUndoVersion(null);
    setDisruptMsg({ type: "success", text: "✅ Visual disruption cleared — original schedule visible again." });
    setTimeout(() => setDisruptMsg(null), 3000);
  };

  const handleUndoDisruption = () => {
    setVisualRows(null);
    setUndoVersion(null);
    setDisruptMsg({ type: "success", text: "↩ Restored — original plan data is back." });
    setTimeout(() => setDisruptMsg(null), 3000);
  };
  const handleWholeDisruption = async () => {
    if (disruptScope !== "day" && disruptScope !== "week") return;
    setWholeLoading(true); setWholeMsg(null);
    try {
      let res;
      if (disruptScope === "day") {
        const m = String(activeDayKey || "").match(/\((\d{2})-(\d{2})-(\d{4})\)/);
        if (!m) {
          setWholeMsg({ type: "error", text: "Could not determine the calendar date for this day — pick a day with a real date attached." });
          setWholeLoading(false);
          return;
        }
        const isoDate = `${m[3]}-${m[2]}-${m[1]}`;
        res = await applyDayDisruption(isoDate, wholeAltType, wholeReason, wholeCustomTitle);
      } else {
        res = await applyWeekDisruption(activeWeek, wholeAltType, wholeReason, wholeCustomTitle);
      }

      if (res?.success && res.plan?.rows) {
        // Group the updated plan's rows by dayId, then push each affected
        // day (within scope) back through onDayRegenerated.
        const byDay = {};
        res.plan.rows.forEach((row) => {
          const dk = row[0];
          if (!byDay[dk]) byDay[dk] = [];
          byDay[dk].push(row);
        });
        Object.entries(byDay).forEach(([dk, rows]) => {
          const parsed = parseDayKey(dk);
          const inScope = disruptScope === "day"
            ? dk === activeDayKey
            : (parsed?.week === activeWeek || (!parsed?.week && activeWeek === 1));
          if (inScope) onDayRegenerated && onDayRegenerated(dk, rows);
        });
        setWholeMsg({ type: "success", text: res.message || "Disruption applied." });
        setWholeReason(""); setWholeCustomTitle("");
      } else {
        setWholeMsg({ type: "error", text: res?.error || "Disruption failed." });
      }
    } catch (err) {
      setWholeMsg({ type: "error", text: err.response?.data?.error || "Disruption failed." });
    }
    setWholeLoading(false);
  };
  const switchWeek = (w) => {
    setActiveWeek(w);
    if (!(byWeekDay[w] || {})[activeDay]) setActiveDay("Monday");
  };

  const weekBtnStyle = (w) => {
    const isActive = activeWeek === w;
    const ia   = iaWeeks.has(w);
    const au   = auWeeks.has(w);
    const mock = mockWeek ? w === mockWeek : false;
    const borderColor = isActive ? C.primary : au ? C.auColor : mock ? C.mockColor : ia ? "#1B4F72" : C.border;
    const bg          = isActive ? C.primary : au ? C.auBg    : mock ? C.mockBg    : ia ? "#E7F0F6" : C.surface;
    const color       = isActive ? "#fff"    : au ? C.auColor : mock ? C.mockColor : ia ? "#143B56" : C.muted;
    return { borderColor, bg, color, ia, au, mock };
  };

  // ── Render ────────────────────────────────────────────────────────────────
  // Reliable tool layout: fixed controls on top, one schedule card scrolls below.
  const panelHeight = `calc(100dvh - ${LAYOUT.navbarHeight}px - 24px)`;

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      height: panelHeight,
      minHeight: 380,
      maxHeight: panelHeight,
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: LAYOUT.radius,
      boxShadow: "0 1px 2px rgba(10,14,23,0.04), 0 8px 24px rgba(10,14,23,0.06)",
      overflow: "hidden",
    }}>
      <style>{`
        .schedule-scroll {
          scrollbar-width: thin;
          scrollbar-color: #C5CDD8 transparent;
        }
        .schedule-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
        .schedule-scroll::-webkit-scrollbar-thumb {
          background: #C5CDD8;
          border-radius: 8px;
        }
        .schedule-scroll::-webkit-scrollbar-thumb:hover { background: #A8B2C2; }
        .schedule-scroll::-webkit-scrollbar-track { background: transparent; }
        .week-grid-table { border-collapse: collapse; font-size: 11.5px; min-width: 100%; }
        .week-grid-table th {
          background: #0B0B0B;
          color: #fff;
          font-weight: 700;
          font-size: 11px;
          text-align: center;
          vertical-align: middle;
          padding: 10px 8px;
          border: 1px solid rgba(27,163,74,0.45);
          box-shadow: inset 0 -3px 0 var(--brand-primary);
          position: sticky;
          top: 0;
          z-index: 3;
          min-width: 190px;
          max-width: 260px;
          line-height: 1.35;
        }
        .week-grid-table th.sticky-col,
        .week-grid-table td.sticky-col {
          position: sticky;
          left: 0;
          min-width: 108px;
          max-width: 120px;
          background: #0B0B0B;
          z-index: 4;
        }
        .week-grid-table td.sticky-col {
          background: #fff;
          z-index: 2;
          font-weight: 800;
          color: #111827;
          text-align: left;
          vertical-align: top;
        }
        .week-grid-table td {
          vertical-align: top;
          text-align: left;
          border: 1px solid #D1D5DB;
          padding: 8px 10px;
          min-width: 220px;
          max-width: 320px;
          background: #fff;
          color: #111827;
          line-height: 1.45;
          word-break: break-word;
        }
        .week-grid-table tr:hover td.sticky-col { background: var(--brand-primary-soft); }
        .week-grid-table td.grid-selected {
          outline: 2px solid var(--brand-primary);
          outline-offset: -2px;
        }
      `}</style>

      {/* ── Fixed top: week / day selectors ── */}
      <div style={{
        flexShrink: 0,
        background: C.surfaceAlt || "#F7F9FC",
        padding: "12px 16px 10px",
        borderBottom: `1px solid ${C.border}`,
      }}>
      {/* Week selector */}
      <div style={{ marginBottom: 10 }}>
        {(memberMeta?.memberName || memberMeta?.name) && (
          <div
            style={{
              marginBottom: 10,
              padding: "8px 12px",
              borderRadius: 10,
              background: C.primarySoft || "#EEF2FF",
              border: `1px solid ${C.primary}33`,
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: "uppercase" }}>
              Student
            </span>
            <span style={{ fontSize: 15, fontWeight: 850, color: C.text }}>
              {memberMeta.memberName || memberMeta.name}
            </span>
            {memberMeta.memberVersion != null && (
              <span style={{ fontSize: 12, fontWeight: 750, color: C.primary }}>
                V{memberMeta.memberVersion}
              </span>
            )}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <p style={{
          fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase",
          letterSpacing: "0.06em", margin: 0,
        }}>
          {`Week ${activeWeek} of ${weeks.length || 1}`} <span style={{ marginLeft: 6, color: C.primary, fontWeight: 800 }}>{plannedDayCount} planned days</span>
        </p>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          {plannedDayCount > 0 && (
            <button
              type="button"
              onClick={() => { setActiveWeek(finalWeek); setActiveDay(finalDay); }}
              style={{ padding: "5px 10px", borderRadius: 7, fontSize: 11, fontWeight: 750, cursor: "pointer", border: `1px solid ${C.primary}`, background: C.primarySoft, color: C.primary }}
            >
              Go to final day
            </button>
          )}
          {[
            { key: "weekGrid", label: "Week table" },
            { key: "dayList", label: "Day list" },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setScheduleLayout(opt.key)}
              style={{
                padding: "5px 10px",
                borderRadius: 7,
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                border: `1.5px solid ${scheduleLayout === opt.key ? C.primary : C.border}`,
                background: scheduleLayout === opt.key ? C.primary : C.surface,
                color: scheduleLayout === opt.key ? "#fff" : C.muted,
              }}
            >
              {opt.label}
            </button>
          ))}
          {scheduleLayout === "weekGrid" ? (
            <TableColumnsMenu
              columns={[
                { id: "day", label: "Day", required: true, icon: "ti-calendar-week" },
                ...weekGrid.columns.map((col) => ({
                  id: col.id,
                  label: col.activity || col.time || col.id,
                  icon: iconForSlotFamily(col.id),
                })),
              ]}
              isOn={gridCols.isOn}
              toggle={gridCols.toggle}
              header={gridCols.header}
              rename={gridCols.rename}
              reset={gridCols.reset}
            />
          ) : (
            <TableColumnsMenu
              columns={DAILY_LIST_COLS}
              isOn={listCols.isOn}
              toggle={listCols.toggle}
              header={listCols.header}
              rename={listCols.rename}
              reset={listCols.reset}
            />
          )}
        </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {weeks.map((w) => {
            const s = weekBtnStyle(w);
            const isActive = activeWeek === w;
            return (
              <button key={w} onClick={() => switchWeek(w)} style={{
                padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: isActive ? 700 : 500,
                border: `1.5px solid ${s.borderColor}`, background: s.bg, color: s.color,
                cursor: "pointer", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 5,
              }}>
                <span style={{ fontWeight: 700 }}>W{w}</span>
                {s.au   && !isActive && <span style={{ fontSize: 9, background: C.auColor   + "22", color: C.auColor,   borderRadius: 4, padding: "1px 5px" }}>AU</span>}
                {s.mock && !isActive && <span style={{ fontSize: 9, background: C.mockColor + "22", color: C.mockColor, borderRadius: 4, padding: "1px 5px" }}>MOCK</span>}
                {s.ia   && !isActive && <span style={{ fontSize: 9, background: "#1B4F7222",         color: "#143B56",   borderRadius: 4, padding: "1px 5px" }}>IA</span>}
              </button>
            );
          })}
        </div>
      </div>
      {scheduleLayout === "weekGrid" && (
        <>
          {editMode && (
            <div style={{
              padding: "8px 12px", fontSize: 12, color: C.primary, fontWeight: 600,
              background: TABLE.editBanner, border: `1px solid ${C.border}`,
              borderRadius: 8, marginBottom: 8,
            }}>
              Edit mode on — click a week-table cell, change the text, then Save.
            </div>
          )}
          <p style={{ fontSize: 12, color: C.muted, margin: "0 0 4px", lineHeight: 1.45 }}>
            One sheet for the whole week — rows are Day 1–5, columns are activities. Weekday names are on Day list.
          </p>
        </>
      )}

      {/* Day tabs — Day list only (week table already shows every day as a row) */}
      {scheduleLayout === "dayList" && (
      <div style={{ display: "flex", gap: 6, marginBottom: lockMsg ? 8 : 0, flexWrap: "wrap" }}>
        {DAY_NAMES.map((day) => {
          const isActive = activeDay === day;
          const dt       = DAY_THEMES[day];
          const count    = (daysInWeek[day] || []).length;
          const dayDate  = calMap ? getDateForWeekDayFE(calMap, activeWeek, day) : null;
          const examEntry = calMap && dayDate ? calMap.find((e) => e.date === dayDate && e.isExam) : null;
          return (
            <button key={day} onClick={() => setActiveDay(day)} style={{
              padding: "7px 14px", borderRadius: 8,
              border: `1.5px solid ${isActive ? dt.color : examEntry ? C.error : C.border}`,
              background: isActive ? dt.light : examEntry ? "#FEF2F2" : C.surface,
              color: isActive ? dt.color : examEntry ? C.error : C.muted,
              fontWeight: isActive ? 700 : 500, fontSize: 13, cursor: "pointer",
              transition: "all 0.15s", display: "flex", alignItems: "center", gap: 6,
            }}>
              {dt.label}
              {examEntry && (
                <span style={{ fontSize: 9, background: "#DC262222", color: C.error, borderRadius: 4, padding: "1px 5px" }}>EXAM</span>
              )}
              {count > 0 && (
                <span style={{
                  fontSize: 11, borderRadius: 99, padding: "1px 7px",
                  background: isActive ? dt.color + "20" : C.bg,
                  color: isActive ? dt.color : C.muted,
                }}>
                  {count} slots
                </span>
              )}
            </button>
          );
        })}
      </div>
      )}

      {/* Lock message banner */}
      {lockMsg && (
        <div style={{
          padding: "8px 12px", fontSize: 12, fontWeight: 600,
          background: lockMsg.type === "error" ? "#FEF2F2" : "#ECFDF5",
          color: lockMsg.type === "error" ? C.error : C.success,
          border: `1px solid ${lockMsg.type === "error" ? "#FCA5A5" : "#6EE7B7"}`,
          borderRadius: 8, marginTop: 4,
        }}>
          {lockMsg.text}
        </div>
      )}
      </div>{/* end week/day chrome */}

      {/* Optional panels — capped so the schedule always keeps usable height */}
      <div style={{
        flexShrink: 0,
        maxHeight: (regenOpen || disruptOpen || undoVersion) ? "32vh" : undefined,
        overflowY: (regenOpen || disruptOpen || undoVersion) ? "auto" : "visible",
        borderBottom: (regenOpen || disruptOpen || undoVersion) ? `1px solid ${C.border}` : "none",
      }}>

      {/* Day header is rendered with the schedule card below */}
      {/* Regenerate panel */}
      {regenOpen && !lockStatus[activeDayKey]?.locked && (
        <div style={{
          background: "#EEF2FF", border: "1px solid #A5B4FC",
          borderLeft: "none", borderRight: "none", padding: "12px 20px",
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.primary, marginBottom: 6 }}>
            ↻ Regenerate Week {activeWeek} – {activeDay} only
          </div>
          <textarea value={regenFeedback} onChange={(e) => setRegenFeedback(e.target.value)}
            placeholder="Optional: what should change? e.g. 'add more project time', 'focus on exam revision'"
            rows={2} style={{
              width: "100%", padding: "8px 10px", border: "1.5px solid #A5B4FC", borderRadius: 6,
              fontSize: 12, boxSizing: "border-box", resize: "vertical", marginBottom: 8,
            }} />
          {regenMsg && (
            <div style={{ fontSize: 12, marginBottom: 8, color: regenMsg.type === "error" ? C.error : C.success }}>
              {regenMsg.text}
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleRegenerateDay} disabled={regenLoading} style={{
              background: C.primary, color: "#fff", border: "none", borderRadius: 6,
              padding: "7px 16px", fontSize: 12, fontWeight: 700,
              cursor: regenLoading ? "not-allowed" : "pointer", opacity: regenLoading ? 0.6 : 1,
            }}>
              {regenLoading ? "Regenerating..." : "✨ Regenerate"}
            </button>
            <button onClick={() => setRegenOpen(false)} style={{
              background: "transparent", color: C.primary, border: "1px solid #A5B4FC",
              borderRadius: 6, padding: "7px 14px", fontSize: 12, cursor: "pointer",
            }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Disruption panel */}
      {disruptOpen && activeDayKey && !lockStatus[activeDayKey]?.locked && (
        <div style={{
          background: "#E7F0F6", border: "1px solid #7BA7C9",
          borderLeft: "none", borderRight: "none", padding: "16px 20px",
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#143B56", marginBottom: 4 }}>
            ⚡ Slot Swap / Meeting Override
          </div>
          <div style={{ fontSize: 12, color: "#143B56", marginBottom: 14 }}>
            Select up to <strong>2 slots</strong>. Swap them with each other, or override with a manual task.
            Original data is never changed — visual only.
          </div>

          {/* Scope selector — slot-level (visual only) vs whole-day / whole-week (real, persisted) */}
          <div style={{ marginBottom: 14 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: "#143B56", marginBottom: 8,
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}>
              What's affected?
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[
                { key: "slot", label: "🕐 Specific slot(s) — this day", hint: "visual only" },
                { key: "day",  label: "📅 Whole day", hint: "saved" },
                { key: "week", label: "🗓️ Whole week", hint: "saved" },
                { key: "cross", label: "🔁 Swap w/ another day", hint: "saved" },
              ].map((opt) => (
                <button key={opt.key}
                  onClick={() => { setDisruptScope(opt.key); setWholeMsg(null); }}
                  style={{
                    display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2,
                    padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                    border: `1.5px solid ${disruptScope === opt.key ? "#1B4F72" : "#E5E7EB"}`,
                    background: disruptScope === opt.key ? "#E7F0F6" : "#fff",
                    color: disruptScope === opt.key ? "#143B56" : "#6B7280",
                    cursor: "pointer",
                  }}>
                  <span>{opt.label}</span>
                  <span style={{ fontSize: 9, fontWeight: 700, color: opt.hint === "saved" ? "#B91C1C" : "#9CA3AF" }}>
                    {opt.hint === "saved" ? "● permanent change" : "○ preview only"}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Whole-day / whole-week panel — real backend change ────────── */}
          {(disruptScope === "day" || disruptScope === "week") && (
            <div style={{
              background: "#fff", border: "1.5px solid #FCA5A5", borderRadius: 10,
              padding: "14px 16px", marginBottom: 14,
            }}>
              <div style={{ fontSize: 12, color: "#B91C1C", fontWeight: 700, marginBottom: 10 }}>
                {disruptScope === "day"
                  ? `This replaces EVERY slot on ${activeDay} (Week ${activeWeek}) and is saved immediately — it isn't a preview.`
                  : `This replaces EVERY slot on EVERY working day of Week ${activeWeek} and is saved immediately — it isn't a preview.`}
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                <div style={{ flex: "1 1 160px" }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#143B56", display: "block", marginBottom: 4 }}>
                    Replace with
                  </label>
                  <select value={wholeAltType} onChange={(e) => setWholeAltType(e.target.value)}
                    style={{
                      width: "100%", padding: "8px 10px", border: "1.5px solid #7BA7C9",
                      borderRadius: 8, fontSize: 12, background: "#F8FAFC",
                    }}>
                    <option value="self_study">📚 Self Study</option>
                    <option value="project_work">💻 Project Work</option>
                    <option value="exam_prep">📝 Exam Preparation</option>
                  </select>
                </div>
                <div style={{ flex: "2 1 220px" }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#143B56", display: "block", marginBottom: 4 }}>
                    Custom label (optional)
                  </label>
                  <input type="text" value={wholeCustomTitle}
                    onChange={(e) => setWholeCustomTitle(e.target.value)}
                    placeholder="e.g. 🏛️ College Function"
                    style={{
                      width: "100%", padding: "8px 10px", border: "1.5px solid #7BA7C9",
                      borderRadius: 8, fontSize: 12, boxSizing: "border-box", background: "#F8FAFC",
                    }} />
                </div>
              </div>

              <input type="text" value={wholeReason}
                onChange={(e) => setWholeReason(e.target.value)}
                placeholder="Reason (e.g. Department Meeting, Principal Announcement, Hackathon)"
                style={{
                  width: "100%", padding: "9px 12px", border: "1.5px solid #7BA7C9",
                  borderRadius: 8, fontSize: 12, marginBottom: 10,
                  boxSizing: "border-box", background: "#F8FAFC",
                }} />

              {wholeMsg && (
                <div style={{
                  fontSize: 12, padding: "8px 12px", borderRadius: 7, marginBottom: 10,
                  background: wholeMsg.type === "error" ? "#FEF2F2" : "#ECFDF5",
                  color: wholeMsg.type === "error" ? "#DC2626" : "#059669",
                  border: `1px solid ${wholeMsg.type === "error" ? "#FCA5A5" : "#6EE7B7"}`,
                }}>
                  {wholeMsg.text}
                </div>
              )}

              <button onClick={handleWholeDisruption} disabled={wholeLoading}
                style={{
                  background: "#B91C1C", color: "#fff", border: "none", borderRadius: 6,
                  padding: "8px 18px", fontSize: 12, fontWeight: 700,
                  cursor: wholeLoading ? "not-allowed" : "pointer", opacity: wholeLoading ? 0.6 : 1,
                }}>
                {wholeLoading
                  ? "Applying..."
                  : disruptScope === "day" ? "⚡ Disrupt Whole Day" : "⚡ Disrupt Whole Week"}
              </button>
            </div>
          )}

          {/* ── Cross-day / cross-week SWAP panel — real backend change ──────
              Exchanges actual content between two days (chosen via the
              side-by-side picker below), as opposed to "day"/"week" above
              which replace content with a placeholder type. */}
          {disruptScope === "cross" && (() => {
            const dayKeyB = (rawDayKeyByWeekDay[crossWeekB] || {})[crossDayB] || null;
            const rowsB   = (byWeekDay[crossWeekB] || {})[crossDayB] || [];
            const crossesWeek = dayKeyB && activeWeek !== crossWeekB;

            // Two-step independent picking: click any slot in column A to
            // "arm" it, then click any slot in column B to complete the pair
            // — no auto-matching by time or position. Clicking an already-
            // paired slot in either column un-pairs it.
            const pickFromA = (timeA) => {
              const existingPair = crossSlotPairs.find((p) => p.timeA === timeA);
              if (existingPair) {
                setCrossSlotPairs((prev) => prev.filter((p) => p.timeA !== timeA));
                setPendingA(null);
                return;
              }
              setPendingA(pendingA === timeA ? null : timeA);
            };

            const pickFromB = (timeB) => {
              const existingPair = crossSlotPairs.find((p) => p.timeB === timeB);
              if (existingPair) {
                setCrossSlotPairs((prev) => prev.filter((p) => p.timeB !== timeB));
                return;
              }
              if (!pendingA) return; // must arm a slot in column A first
              setCrossSlotPairs((prev) => [...prev, { timeA: pendingA, timeB }]);
              setPendingA(null);
            };

            const handleCrossSwap = async () => {
              if (!activeDayKey || !dayKeyB) return;
              if (lockStatus[activeDayKey]?.locked || lockStatus[dayKeyB]?.locked) {
                setCrossMsg({ type: "error", text: "One of the two days is locked — unlock it first." });
                return;
              }
              if (crossMode === "slots" && crossSlotPairs.length === 0) {
                setCrossMsg({ type: "error", text: "Pick at least one matching slot pair from both columns." });
                return;
              }
              setCrossLoading(true); setCrossMsg(null);
              try {
                const res = await swapAcrossDays(
                  activeDayKey, dayKeyB, crossMode,
                  crossMode === "slots" ? crossSlotPairs : []
                );
                if (res.success) {
                  setLastSwapId(res.swapId);
                  setCrossMsg({ type: "success", text: res.message });
                  setCrossSlotPairs([]);
                  setPendingA(null);
                  
                  if (res.dayA?.rows) onDayRegenerated && onDayRegenerated(res.dayA.dayId, res.dayA.rows);
                  if (res.dayB?.rows) onDayRegenerated && onDayRegenerated(res.dayB.dayId, res.dayB.rows);

                  const r = await getDailyStatus();
                  if (r.success) {
                    const map = {};
                    (r.days || []).forEach((d) => { map[d.dayId] = { locked: d.locked, completed: d.completed }; });
                    setLockStatus(map);
                  }
                } else {
                  setCrossMsg({ type: "error", text: res.error || "Swap failed." });
                }
              } catch (err) {
                setCrossMsg({ type: "error", text: err.response?.data?.error || "Swap failed." });
              } finally {
                setCrossLoading(false);
              }
            };

            const handleUndoCrossSwap = async () => {
              if (!lastSwapId) return;
              setCrossLoading(true);
              try {
                const res = await undoSwapAcrossDays(lastSwapId);
                if (res.success) {
                  if (res.dayA?.rows) onDayRegenerated && onDayRegenerated(res.dayA.dayId, res.dayA.rows);
                  if (res.dayB?.rows) onDayRegenerated && onDayRegenerated(res.dayB.dayId, res.dayB.rows);
                }
                setCrossMsg({ type: res.success ? "success" : "error", text: res.message || res.error });
                setLastSwapId(null);
              } finally {
                setCrossLoading(false);
              }
            };

            return (
              <div style={{
                background: "#fff", border: "1.5px solid #7BA7C9", borderRadius: 10,
                padding: "14px 16px", marginBottom: 14,
              }}>
                <div style={{ fontSize: 12, color: "#143B56", fontWeight: 700, marginBottom: 10 }}>
                  Swap content between two days — saved immediately, not a preview.
                </div>

                {/* Day B picker */}
                <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: "#143B56", fontWeight: 700 }}>{activeDayKey} ↔</span>
                  <select value={crossWeekB} onChange={(e) => { setCrossWeekB(Number(e.target.value)); setCrossSlotPairs([]); setPendingA(null); }}
                    style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #E5E7EB", fontSize: 12 }}>
                    {weeks.map((w) => <option key={w} value={w}>Week {w}</option>)}
                  </select>
                  <select value={crossDayB} onChange={(e) => { setCrossDayB(e.target.value); setCrossSlotPairs([]); setPendingA(null); }}
                    style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #E5E7EB", fontSize: 12 }}>
                    {DAY_ORDER.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  {dayKeyB && lockStatus[dayKeyB]?.locked && (
                    <span style={{ fontSize: 12, color: "#DC2626" }}>🔒 locked — cannot swap</span>
                  )}
                </div>

                {crossesWeek && (
                  <div style={{ fontSize: 12, color: "#143B56", marginBottom: 10, background: "#E7F0F6", padding: "6px 10px", borderRadius: 6 }}>
                    ⚠️ Cross-week swap (Week {activeWeek} ↔ Week {crossWeekB}). Concept sequencing isn't
                    auto-validated — double check no later-week material gets pulled earlier than it should.
                  </div>
                )}

                {/* Mode toggle */}
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  {[{ key: "slots", label: "Specific Slot(s)" }, { key: "whole", label: "Whole Day" }].map((opt) => (
                    <button key={opt.key} onClick={() => { setCrossMode(opt.key); setCrossSlotPairs([]); setPendingA(null); }}
                      style={{
                        padding: "5px 10px", borderRadius: 6, fontSize: 12,
                        border: `1.5px solid ${crossMode === opt.key ? "#1B4F72" : "#E5E7EB"}`,
                        background: crossMode === opt.key ? "#E7F0F6" : "#fff",
                        color: crossMode === opt.key ? "#143B56" : "#6B7280",
                        cursor: "pointer",
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </div>

                {/* Side-by-side slot pickers — independent selection */}
                {dayKeyB && crossMode === "slots" && (
                  <>
                    <div style={{ fontSize: 11, color: "#143B56", marginBottom: 6 }}>
                      {pendingA
                        ? <>Pick a slot from <strong>{dayKeyB}</strong> to pair with <strong>{pendingA}</strong>.</>
                        : <>Click a slot on the left, then a slot on the right to pair them. You can pick any two periods — they don't need to match.</>}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#143B56", marginBottom: 4 }}>{activeDayKey}</div>
                        {dayRows.map((row) => {
                          const time = row[1];
                          const pair = crossSlotPairs.find((p) => p.timeA === time);
                          const isArmed = pendingA === time;
                          return (
                            <div key={time} onClick={() => pickFromA(time)}
                              style={{
                                padding: "6px 8px", borderRadius: 6, marginBottom: 4, fontSize: 12, cursor: "pointer",
                                background: pair ? "#E7F0F6" : isArmed ? "#BFDBFE" : "#fff",
                                border: `1.5px solid ${pair ? "#1B4F72" : isArmed ? "#143B56" : "#E5E7EB"}`,
                              }}>
                              <strong>{formatClock(time) || time}</strong> — {row[2]}
                              {pair && <span style={{ float: "right", fontSize: 10, color: "#143B56" }}>↔ {pair.timeB}</span>}
                              {isArmed && !pair && <span style={{ float: "right", fontSize: 10, color: "#143B56" }}>selected…</span>}
                            </div>
                          );
                        })}
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#143B56", marginBottom: 4 }}>{dayKeyB}</div>
                        {rowsB.map((row) => {
                          const time = row[1];
                          const pair = crossSlotPairs.find((p) => p.timeB === time);
                          return (
                            <div key={time} onClick={() => pickFromB(time)}
                              style={{
                                padding: "6px 8px", borderRadius: 6, marginBottom: 4, fontSize: 12,
                                cursor: pendingA || pair ? "pointer" : "default",
                                background: pair ? "#E7F0F6" : "#fff",
                                border: `1.5px solid ${pair ? "#1B4F72" : "#E5E7EB"}`,
                                opacity: pendingA || pair ? 1 : 0.7,
                              }}>
                              <strong>{formatClock(time) || time}</strong> — {row[2]}
                              {pair && <span style={{ float: "right", fontSize: 10, color: "#143B56" }}>↔ {pair.timeA}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {crossSlotPairs.length > 0 && (
                      <div style={{ fontSize: 11, color: "#78350F", marginBottom: 10 }}>
                        {crossSlotPairs.length} pair(s) selected:{" "}
                        {crossSlotPairs.map((p, idx) => (
                          <span key={idx} style={{ marginRight: 8 }}>{formatClock(p.timeA) || p.timeA} ↔ {formatClock(p.timeB) || p.timeB}</span>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {crossMsg && (
                  <div style={{
                    padding: "8px 10px", borderRadius: 6, fontSize: 12, marginBottom: 10,
                    background: crossMsg.type === "error" ? "#FEF2F2" : "#ECFDF5",
                    color: crossMsg.type === "error" ? "#DC2626" : "#059669",
                    border: `1px solid ${crossMsg.type === "error" ? "#FCA5A5" : "#6EE7B7"}`,
                  }}>
                    {crossMsg.text}
                  </div>
                )}

                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={handleCrossSwap} disabled={crossLoading || !dayKeyB}
                    style={{
                      padding: "8px 16px", borderRadius: 6, background: "#1B4F72", color: "#fff",
                      border: "none", fontSize: 12, fontWeight: 700,
                      cursor: crossLoading ? "not-allowed" : "pointer", opacity: crossLoading ? 0.6 : 1,
                    }}>
                    {crossLoading ? "Swapping..." : "🔁 Apply Swap"}
                  </button>
                  {lastSwapId && (
                    <button onClick={handleUndoCrossSwap} disabled={crossLoading}
                      style={{
                        padding: "8px 16px", borderRadius: 6, background: "#fff", color: "#143B56",
                        border: "1px solid #1B4F72", fontSize: 12, fontWeight: 600,
                        cursor: crossLoading ? "not-allowed" : "pointer",
                      }}>
                      ↩ Undo Last Swap
                    </button>
                  )}
                </div>

                {/* Swap history log for the viewed day */}
                {swapHistory.length > 0 && (
                  <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px dashed #BFDBFE" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#143B56", marginBottom: 6 }}>
                      Swap history for this day
                    </div>
                    {swapHistory.map((s) => {
                      const otherDay = s.dayKeyA === activeDayKey ? s.dayKeyB : s.dayKeyA;
                      const otherWeek = s.dayKeyA === activeDayKey ? s.weekB : s.weekA;
                      return (
                        <div key={s._id} style={{
                          fontSize: 11, color: "#78350F", padding: "5px 8px", marginBottom: 4,
                          background: "#fff", borderRadius: 5, border: "1px solid #BFDBFE",
                          display: "flex", justifyContent: "space-between", gap: 8,
                        }}>
                          <span>
                            🔁 {s.mode === "whole" ? "Whole day" : `${s.swappedTimes?.length || 0} slot(s)`} ↔{" "}
                            <strong>{otherDay}</strong>{s.crossWeek ? ` (Week ${otherWeek})` : ""}
                          </span>
                          <span style={{ color: "#A8A29E", whiteSpace: "nowrap" }}>
                            {new Date(s.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {disruptScope === "slot" && (
          <>
          {/* Mode toggle */}
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {[
              { key: "swap",   label: "🔀 Swap 2 Slots" },
              { key: "manual", label: "✏️ Manual Task"  },
            ].map((opt) => (
              <button key={opt.key}
                onClick={() => { setDisruptMode(opt.key); setSelectedSlots([]); setManualTasks({}); }}
                style={{
                  padding: "6px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                  border: `1.5px solid ${disruptMode === opt.key ? "#1B4F72" : "#E5E7EB"}`,
                  background: disruptMode === opt.key ? "#E7F0F6" : "#fff",
                  color: disruptMode === opt.key ? "#143B56" : "#6B7280",
                  cursor: "pointer",
                }}>
                {opt.label}
              </button>
            ))}
          </div>

          {/* Slot selector */}
          <div style={{ marginBottom: 14 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: "#143B56", marginBottom: 8,
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}>
              {disruptMode === "swap"
                ? `Select exactly 2 slots to swap (${selectedSlots.length}/2 selected)`
                : `Select slot(s) to override (${selectedSlots.length} selected)`}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {dayRows.map((row, ri) => {
                const timeVal   = String(row[1] || "").trim();
                const activity  = String(row[2] || "");
                const isSelected = selectedSlots.includes(timeVal);
                const selIdx     = selectedSlots.indexOf(timeVal);
                const isDisabled = !isSelected && disruptMode === "swap" && selectedSlots.length >= 2;
                return (
                  <button key={ri} onClick={() => !isDisabled && toggleSlot(timeVal)} style={{
                    display: "flex", alignItems: "center", gap: 6,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    padding: "7px 12px", borderRadius: 8, fontSize: 12,
                    fontWeight: isSelected ? 700 : 500,
                    border: `1.5px solid ${isSelected ? "#1B4F72" : "#E5E7EB"}`,
                    background: isSelected ? "#E7F0F6" : "#fff",
                    color: isSelected ? "#143B56" : "#374151",
                    opacity: isDisabled ? 0.4 : 1, transition: "all 0.15s",
                  }}>
                    {isSelected && (
                      <span style={{
                        width: 18, height: 18, borderRadius: "50%", background: "#1B4F72",
                        color: "#fff", fontSize: 10, fontWeight: 800,
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        {selIdx + 1}
                      </span>
                    )}
                    <span style={{ color: "#1B4F72", fontWeight: 700 }}>{timeVal}</span>
                    <span style={{ color: "#6B7280" }}>
                      {activity.slice(0, 26)}{activity.length > 26 ? "…" : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Swap preview */}
          {disruptMode === "swap" && selectedSlots.length === 2 && (() => {
            const [tA, tB] = selectedSlots;
            const rowA = dayRows.find((r) => String(r[1] || "").trim() === tA);
            const rowB = dayRows.find((r) => String(r[1] || "").trim() === tB);
            return (
              <div style={{
                background: "#fff", border: "1px solid #7BA7C9",
                borderRadius: 10, padding: "12px 14px", marginBottom: 14,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#143B56", marginBottom: 8, textTransform: "uppercase" }}>
                  Preview — after swap
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, background: "#EEF2FF", borderRadius: 8, padding: "8px 12px" }}>
                    <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 2 }}>{tA} will get →</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#4338CA" }}>{String(rowB?.[2] || "")}</div>
                    <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>
                      {String(rowB?.[3] || "").slice(0, 60)}{(rowB?.[3] || "").length > 60 ? "…" : ""}
                    </div>
                  </div>
                  <span style={{ fontSize: 20 }}>🔀</span>
                  <div style={{ flex: 1, background: "#EEF2FF", borderRadius: 8, padding: "8px 12px" }}>
                    <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 2 }}>{tB} will get →</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#4338CA" }}>{String(rowA?.[2] || "")}</div>
                    <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>
                      {String(rowA?.[3] || "").slice(0, 60)}{(rowA?.[3] || "").length > 60 ? "…" : ""}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Manual task inputs */}
          {disruptMode === "manual" && selectedSlots.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: "#143B56", marginBottom: 8, textTransform: "uppercase",
              }}>
                Enter tasks for selected slots
              </div>
              {selectedSlots.map((t) => (
                <div key={t} style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#1B4F72", display: "block", marginBottom: 4 }}>
                    {t} — instead of: {String(dayRows.find((r) => String(r[1] || "").trim() === t)?.[2] || "")}
                  </label>
                  <textarea value={manualTasks[t] || ""}
                    onChange={(e) => setManualTasks((p) => ({ ...p, [t]: e.target.value }))}
                    placeholder="Type the manual task for this slot..."
                    rows={2} style={{
                      width: "100%", padding: "8px 10px", border: "1.5px solid #7BA7C9", borderRadius: 8,
                      fontSize: 12, boxSizing: "border-box", resize: "vertical", background: "#F8FAFC",
                    }} />
                </div>
              ))}
            </div>
          )}

          {/* Reason field */}
          {selectedSlots.length > 0 && (
            <input type="text" value={disruptReason}
              onChange={(e) => setDisruptReason(e.target.value)}
              placeholder="Reason (e.g. Department Meeting, Principal Announcement) — optional"
              style={{
                width: "100%", padding: "9px 12px", border: "1.5px solid #7BA7C9",
                borderRadius: 8, fontSize: 12, marginBottom: 12,
                boxSizing: "border-box", background: "#F8FAFC",
              }} />
          )}

          {/* Disruption status message (slot scope) */}
          {disruptMsg && (
            <div style={{
              fontSize: 12, padding: "8px 12px", borderRadius: 7, marginBottom: 10,
              background: disruptMsg.type === "error" ? "#FEF2F2" : "#ECFDF5",
              color: disruptMsg.type === "error" ? "#DC2626" : "#059669",
              border: `1px solid ${disruptMsg.type === "error" ? "#FCA5A5" : "#6EE7B7"}`,
            }}>
              {disruptMsg.text}
            </div>
          )}
          </>
          )}

          {/* Disruption action buttons */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {disruptScope === "slot" && (
              <button onClick={handleDisrupt}
                disabled={
                  selectedSlots.length === 0 ||
                  (disruptMode === "swap" && selectedSlots.length < 2) ||
                  (disruptMode === "manual" && !selectedSlots.some((t) => manualTasks[t]?.trim()))
                }
                style={{
                  background: selectedSlots.length === 0 ? "#9CA3AF" : "#1B4F72",
                  color: "#fff", border: "none", borderRadius: 6, padding: "8px 18px",
                  fontSize: 12, fontWeight: 700,
                  cursor: selectedSlots.length === 0 ? "not-allowed" : "pointer",
                  opacity: (selectedSlots.length === 0 || (disruptMode === "swap" && selectedSlots.length < 2)) ? 0.5 : 1,
                }}>
                {disruptMode === "swap"
                  ? selectedSlots.length < 2
                    ? `🔀 Select ${2 - selectedSlots.length} more slot${selectedSlots.length === 1 ? "" : "s"}`
                    : "🔀 Apply Swap"
                  : `✏️ Apply Manual Task${selectedSlots.length !== 1 ? "s" : ""}`}
              </button>
            )}
            {visualRows && (
              <button onClick={handleRemoveDisruption} style={{
                background: "#DC2626", color: "#fff", border: "none",
                borderRadius: 6, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}>
                ↩ Restore Original
              </button>
            )}
            <button onClick={() => { setDisruptOpen(false); setSelectedSlots([]); setManualTasks({}); setDisruptMsg(null); setWholeMsg(null); }}
              style={{
                background: "transparent", color: "#143B56", border: "1px solid #7BA7C9",
                borderRadius: 6, padding: "8px 14px", fontSize: 12, cursor: "pointer",
              }}>
              Close
            </button>
          </div>

          {visualRows && (
            <div style={{
              marginTop: 12, padding: "8px 12px", background: "#E7F0F6", borderRadius: 8,
              fontSize: 11, color: "#143B56", border: "1px solid #7BA7C9",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <span>⚡</span>
              <span>
                <strong>Visual disruption active</strong> — table shows modified view.
                Original plan data is safe. Click "Restore Original" to revert.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Undo banner */}
      {undoVersion && (
        <div style={{
          background: "#E7F0F6", border: "1px solid #7BA7C9",
          borderLeft: "none", borderRight: "none",
          padding: "10px 20px", display: "flex",
          justifyContent: "space-between", alignItems: "center", gap: 12,
        }}>
          <span style={{ fontSize: 12, color: "#143B56", fontWeight: 600 }}>
            ↩ Disruption applied — "{undoVersion.reason}". Click Undo to restore original content.
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleUndoDisruption} style={{
              background: "#1B4F72", color: "#fff", border: "none", borderRadius: 6,
              padding: "5px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}>
              ↩ Restore Original
            </button>
            <button onClick={() => setUndoVersion(null)} style={{
              background: "transparent", color: "#143B56", border: "1px solid #7BA7C9",
              borderRadius: 6, padding: "5px 10px", fontSize: 12, cursor: "pointer",
            }}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      </div>{/* end optional panels */}

      {/* ── Schedule card: action bar stays put; rows scroll ── */}
      <div style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        background: C.surface,
      }}>
        {/* Day action bar — light contrast like weekly / homework tools */}
        <div style={{
          flexShrink: 0,
          background: activeDayKey && lockStatus[activeDayKey]?.locked
            ? "#143B56"
            : "#F8FAFC",
          padding: "11px 16px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 8,
          borderBottom: `1px solid ${C.border}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {activeDayKey && lockStatus[activeDayKey]?.locked && (
              <span style={{ fontSize: 16 }}>🔒</span>
            )}
            <span style={{
              color: activeDayKey && lockStatus[activeDayKey]?.locked ? "#fff" : C.text,
              fontWeight: 700, fontSize: 15,
            }}>
              Week {activeWeek} · {theme.label}
              {realDate && (
                <span style={{ fontWeight: 500, fontSize: 12, marginLeft: 8, color: C.muted }}>({toDD(realDate)})</span>
              )}
            </span>
            {activeDayKey && lockStatus[activeDayKey]?.locked && (
              <span style={{
                fontSize: 11, color: "#BFDBFE", fontWeight: 700,
                background: "rgba(255,255,255,0.15)", borderRadius: 6, padding: "2px 10px",
              }}>
                LOCKED — protected from leave/regen
              </span>
            )}
            {editing && (
              <span style={{
                fontSize: 11, color: C.primaryDark, fontWeight: 700,
                background: "#fff", border: `1px solid ${C.primary}55`, borderRadius: 6, padding: "2px 10px",
              }}>
                Edit mode — click a task to correct / add
              </span>
            )}
            {activeDay === "Friday" && !lockStatus[activeDayKey]?.locked && (
              <span style={{
                fontSize: 12, color: C.primaryDark, fontWeight: 600,
                background: "#fff", borderRadius: 6, padding: "2px 10px",
              }}>
                {fridayLabel}
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {Object.keys(daysInWeek).length > 0 && (
              <span style={{ color: C.muted, fontSize: 12, fontWeight: 600 }}>
                {doneRows.size}/{dayRows.length} done
              </span>
            )}
            <span style={{ color: C.muted, fontSize: 12 }}>{dayRows.length} activities</span>

            {plan && (
              <button onClick={() => handleSync(activeDayKey)} disabled={lockLoading} style={{
                background: "#fff", color: C.primaryDark, border: `1.5px solid ${C.primary}`,
                borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 700,
                cursor: lockLoading ? "not-allowed" : "pointer",
              }}>
                {lockLoading ? "..." : synced ? "🔄 Re-sync" : "🔄 Sync to DB"}
              </button>
            )}

            {activeDayKey ? (
              lockStatus[activeDayKey]?.locked ? (
                <button onClick={() => handleUnlockDay(activeDayKey)} disabled={lockLoading} style={{
                  background: "rgba(255,255,255,0.22)", color: "#fff", border: "1px solid rgba(255,255,255,0.55)",
                  borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 700,
                  cursor: lockLoading ? "not-allowed" : "pointer",
                }}>
                  🔓 Unlock
                </button>
              ) : (
                <button onClick={() => handleLockDay(activeDayKey)} disabled={lockLoading} style={{
                  background: "#fff", color: C.text, border: `1.5px solid ${C.borderStrong}`,
                  borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 700,
                  cursor: lockLoading ? "not-allowed" : "pointer",
                }}>
                  🔒 Lock Day
                </button>
              )
            ) : (
              <span style={{ color: C.muted, fontSize: 11 }}>
                Select a day tab to Lock / Disruption
              </span>
            )}

            {activeDayKey && !lockStatus[activeDayKey]?.locked && (
              <button onClick={() => setRegenOpen(!regenOpen)} style={{
                background: regenOpen ? C.primary : "#fff",
                color: regenOpen ? "#fff" : C.primaryDark,
                border: `1.5px solid ${C.primary}`,
                borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}>
                ↻ Regenerate
              </button>
            )}

            {activeDayKey && !lockStatus[activeDayKey]?.locked && (
              <button onClick={() => { setDisruptOpen(!disruptOpen); setRegenOpen(false); }} style={{
                background: disruptOpen ? C.success : "#fff",
                color: disruptOpen ? "#fff" : C.success,
                border: `1.5px solid ${C.success}`,
                borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}>
                ⚡ Meeting / Disruption
              </button>
            )}
          </div>
        </div>

        {/* Scrollable rows only */}
        <div
          className="schedule-scroll"
          style={{
            flex: 1,
            minHeight: 0,
            overflowX: "auto",
            overflowY: "auto",
            background: "#fff",
          }}
        >
        {scheduleLayout === "weekGrid" ? (
        <table className="week-grid-table">
          <thead>
            <tr>
              {gridCols.isOn("day") && (
                <th className="sticky-col">{gridCols.header("day", "Day")}</th>
              )}
              {weekGrid.columns.filter((col) => gridCols.isOn(col.id)).map((col) => (
                  <th key={col.id}>
                    <div>{col.time}</div>
                    <div style={{ fontWeight: 600, opacity: 0.95, marginTop: 2 }}>
                      {gridCols.header(col.id, col.activity)}
                    </div>
                  </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weekGrid.days.length === 0 ? (
              <tr>
                <td colSpan={Math.max(1, weekGrid.columns.filter((c) => gridCols.isOn(c.id)).length + (gridCols.isOn("day") ? 1 : 0))} style={{ padding: 40, textAlign: "center", color: C.muted }}>
                  Holiday / Vacation — No data for Week {activeWeek}
                </td>
              </tr>
            ) : (
              weekGrid.days.map((d) => {
                const isActiveDay = activeDay === d.dayName;
                const dayDate = calMap ? getDateForWeekDayFE(calMap, activeWeek, d.dayName) : null;
                return (
                  <tr key={d.dayName}>
                    {gridCols.isOn("day") && (
                    <td
                      className="sticky-col"
                      onClick={() => setActiveDay(d.dayName)}
                      style={{
                        cursor: "pointer",
                        background: isActiveDay ? TABLE.week.selected : "#fff",
                        borderLeft: isActiveDay ? `3px solid ${TABLE.week.selectedBorder}` : "1px solid #D1D5DB",
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: "-0.02em" }}>
                        {dayGridLabel(activeWeek, d.dayName)}
                      </div>
                      {dayDate && (
                        <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginTop: 4 }}>
                          {toDD(dayDate)}
                        </div>
                      )}
                    </td>
                    )}
                    {weekGrid.columns.filter((col) => gridCols.isOn(col.id)).map((col) => {
                      const slot = d.byId[col.id];
                      const selected = selectedGrid?.dayName === d.dayName && selectedGrid?.slotId === col.id;
                      if (!slot) {
                        return (
                          <td
                            key={col.id}
                            className={selected ? "grid-selected" : ""}
                            style={{ background: col.added ? "#fff" : "#FAFAFA", color: col.added ? C.text : "#9CA3AF" }}
                          >
                            {restSlotPlain(col.activity) || (col.added ? (col.notes || col.activity || "—") : "—")}
                          </td>
                        );
                      }
                      const activity = String(slot.activity || col.activity || "");
                      const restOnly = restSlotPlain(activity);
                      const slotOv = resolveSlotOverride(activity, slotOverrides);
                      const rawContent = restOnly || (slot.content != null && String(slot.content).trim() !== ""
                        ? slot.content
                        : slot.row?.[3]);
                      const resourced = restOnly
                        ? { text: restOnly, youtube: "", article: "" }
                        : withLearningResources(
                            activity,
                            displayRowContent(
                              activity,
                              connectDayCell(
                                activity,
                                displaySlotContent(rawContent, slotOv),
                                connectCtxFor(daysInWeek[d.dayName] || [], d.dayName)
                              )
                            ),
                            videoPlan.get(videoKey(slot.row))
                          );
                      const lines = parseTaskLines(resourced.text);
                      const gridEditing =
                        editing &&
                        editDraft?.dayId === String(slot.row[0] || "") &&
                        String(editDraft?.time || "").trim() === String(slot.row[1] || "").trim();
                      return (
                        <td
                          key={col.id}
                          className={selected ? "grid-selected" : ""}
                          onClick={() => {
                            setActiveDay(d.dayName);
                            setSelectedGrid({ dayName: d.dayName, slotId: col.id });
                            if (!editing || !onPlanChange) return;
                            setEditDraft({
                              dayId: String(slot.row[0] || ""),
                              time: String(slot.row[1] || "").trim(),
                              activity,
                              content: String(slot.row[3] || "").replace(/\\n/g, "\n"),
                            });
                          }}
                          style={{ cursor: editing ? "text" : "pointer" }}
                        >
                          {gridEditing ? (
                            <div onClick={(e) => e.stopPropagation()}>
                              <input
                                value={editDraft.activity}
                                onChange={(e) => setEditDraft((d0) => ({ ...d0, activity: e.target.value }))}
                                style={{
                                  width: "100%", padding: "4px 6px", fontSize: 11, marginBottom: 6,
                                  border: `1.5px solid ${C.primary}`, borderRadius: 6, boxSizing: "border-box",
                                }}
                              />
                              <textarea
                                value={editDraft.content}
                                onChange={(e) => setEditDraft((d0) => ({ ...d0, content: e.target.value }))}
                                rows={8}
                                style={{
                                  width: "100%", padding: "6px 8px", fontSize: 11.5, lineHeight: 1.45,
                                  border: `1.5px solid ${C.primary}`, borderRadius: 6, boxSizing: "border-box",
                                  fontFamily: "inherit", resize: "vertical",
                                }}
                              />
                              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                                <button type="button" onClick={saveRowEdit} style={{
                                  background: C.primary, color: "#fff", border: "none", borderRadius: 6,
                                  padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                                }}>
                                  Save
                                </button>
                                <button type="button" onClick={() => setEditDraft(null)} style={{
                                  background: "transparent", color: C.muted, border: `1px solid ${C.border}`,
                                  borderRadius: 6, padding: "4px 8px", fontSize: 11, cursor: "pointer",
                                }}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {slot.activity && slot.activity !== col.activity && (
                                <div style={{ display: "inline-flex", marginBottom: 5, padding: "2px 7px", borderRadius: 999, background: "#E7F0F6", color: "#143B56", fontSize: 10.5, fontWeight: 750 }}>
                                  {slot.activity}
                                </div>
                              )}
                              {editing && (
                                <div style={{ fontSize: 10, color: C.primary, fontWeight: 700, marginBottom: 4 }}>✎ edit</div>
                              )}
                              {lines.map((line, li) => (
                                line.stripped ? (
                                  <div key={li} style={{ margin: "2px 0" }}>
                                    <RichLineText text={line.text} />
                                  </div>
                                ) : (
                                  <div key={li} style={{ height: 4 }} />
                                )
                              ))}
                              <LearningResourceChips youtube={resourced.youtube} youtubeTitle={resourced.youtubeTitle} article={resourced.article} />
                            </>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        ) : (
        <>
        <div style={{
          padding: "10px 16px",
          borderBottom: `1px solid ${C.border}`,
          background: (DAY_THEMES[activeDay] || DAY_THEMES.Monday).light,
          display: "flex",
          alignItems: "baseline",
          gap: 10,
          flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>
            {(DAY_THEMES[activeDay] || {}).label || activeDay}
          </span>
          <span style={{ fontSize: 13, fontWeight: 650, color: C.muted }}>
            {dayGridLabel(activeWeek, activeDay)}
            {calMap && getDateForWeekDayFE(calMap, activeWeek, activeDay)
              ? ` · ${toDD(getDateForWeekDayFE(calMap, activeWeek, activeDay))}`
              : ""}
          </span>
        </div>
        <div style={{ padding: "0 0 12px" }}>
            {(() => {
              const displayCells = [...cellsForWeekDay(weekGrid, activeDay)];
              if (
                dayRows.length &&
                !displayCells.some((c) => isHomeworkActivity(c.activity)) &&
                !dayRows.some((r) => /holiday|vacation/i.test(String(r[2] || "")))
              ) {
                const existing = dayRows.find((r) => isHomeworkActivity(r[2]));
                const existingText = String(existing?.[3] || "").replace(/\\n/g, "\n").trim();
                const useExisting =
                  /^(LeetCode|Exercism|LinkedIn|Agent Workbench|Case study)\b/im.test(existingText) &&
                  !/Event Logger Agent \(Easy\)(?!\s*—\s*Day)/.test(existingText);
                displayCells.push({
                  colId: "homework",
                  time: "Tonight",
                  activity: "Tonight's Homework",
                  content: useExisting ? existingText : buildTonightHomework(dayRows),
                  row: existing || null,
                });
              }
              const visibleCols = DAILY_LIST_COLS.filter((c) => listCols.isOn(c.id));
              const gridTemplate = visibleCols
                .map((c) => (c.id === "content" ? "minmax(220px, 1fr)" : c.id === "check" ? "44px" : c.id === "time" ? "128px" : "168px"))
                .join(" ");
              return displayCells.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: C.muted }}>
                  Holiday / Vacation — No data for Week {activeWeek} – {activeDay}
                </div>
              ) : (
                <div style={{ padding: "12px 12px 8px" }}>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: gridTemplate,
                    gap: 8,
                    padding: "6px 14px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    color: TABLE.headText,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}>
                    {visibleCols.map((col) => (
                      <div key={col.id}>{listCols.header(col.id, col.label)}</div>
                    ))}
                  </div>
                {displayCells.map((cell, ri) => {
                  const activityTitle   = String(cell.activity || "");
                  const rowIndex        = (() => {
                    const hit = origIndexForCell(cell, dayRows);
                    return hit >= 0 ? hit : ri;
                  })();
                  const restOnly = restSlotPlain(activityTitle);
                  const contentSource = restOnly || String(cell.content || "").replace(/\\n/g, "\n");
                  const resourced = restOnly
                    ? { text: restOnly, youtube: "", article: "" }
                    : withLearningResources(
                        activityTitle,
                        displayRowContent(
                          activityTitle,
                          connectDayCell(activityTitle, contentSource, connectCtxFor(dayRows, activeDay))
                        ),
                        videoPlan.get(videoKey(cell.row))
                      );
                  const displayText = resourced.text;
                  const rs              = getRowStyle(activityTitle);
                  const isDone          = doneRows.has(rowIndex);
                  const isVisuallySwapped = visualRows && cell.row && JSON.stringify(cell.row) !== JSON.stringify(dayRows[rowIndex]);
                  return (
                    <div key={ri} style={{
                      display: "grid",
                      gridTemplateColumns: gridTemplate,
                      gap: 10,
                      alignItems: "start",
                      padding: "14px 16px",
                      marginBottom: 10,
                      borderRadius: 12,
                      border: `1px solid ${isVisuallySwapped ? "#B7D0E0" : C.border}`,
                      background: isVisuallySwapped ? "#E7F0F6" : isDone ? "#F0FDF4" : "#fff",
                      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                      opacity: isDone ? 0.72 : 1,
                    }}>
                      {listCols.isOn("check") && (
                      <div style={{ textAlign: "center", paddingTop: 2 }}>
                        <input type="checkbox" checked={isDone} onChange={() => handleToggleDone(rowIndex)}
                          title={isDone ? "Uncheck to clear all strikes in this activity" : "Check to mark every task in this activity done"}
                          style={{ width: 16, height: 16, cursor: "pointer" }} />
                      </div>
                      )}
                      {listCols.isOn("time") && (
                      <div style={{
                        color: isVisuallySwapped ? "#1B4F72" : rs.accent,
                        fontWeight: 800, fontSize: 12.5,
                        whiteSpace: "normal",
                        lineHeight: 1.35,
                      }}>
                        {cell.time}
                        {isVisuallySwapped && (
                          <span style={{
                            fontSize: 9, marginLeft: 4, background: "#E7F0F6",
                            color: "#143B56", borderRadius: 4, padding: "1px 5px",
                          }}>⚡</span>
                        )}
                      </div>
                      )}
                      {listCols.isOn("activity") && (
                      <div style={{
                        fontWeight: 700, fontSize: 13,
                        textDecoration: isDone ? "line-through" : "none",
                      }}>
                        {editing && editDraft?.ri === rowIndex ? (
                          <input
                            value={editDraft.activity}
                            onChange={(e) => setEditDraft((d) => ({ ...d, activity: e.target.value }))}
                            style={{
                              width: "100%", padding: "6px 8px", fontSize: 12,
                              border: `1.5px solid ${C.primary}`, borderRadius: 6, boxSizing: "border-box",
                            }}
                          />
                        ) : (
                          <div
                            style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: editing ? "pointer" : "default", flexWrap: "wrap" }}
                            onClick={() => {
                              if (!editing) return;
                              setEditDraft({
                                ri: rowIndex,
                                activity: activityTitle,
                                content: String(contentSource || "").replace(/\\n/g, "\n"),
                              });
                            }}
                            title={editing ? "Click to edit this slot" : undefined}
                          >
                            <span style={{
                              width: 8, height: 8, borderRadius: "50%", background: rs.dot,
                              flexShrink: 0, marginTop: 5,
                            }} />
                            <span style={{ color: rs.accent }}>{activityTitle}</span>
                            {editing && (
                              <span style={{ fontSize: 10, color: C.muted, marginLeft: 4 }}>✎</span>
                            )}
                          </div>
                        )}
                      </div>
                      )}
                      {listCols.isOn("content") && (
                      <div style={{
                        color: C.text, fontSize: 13,
                        lineHeight: 1.65,
                        wordBreak: "break-word",
                      }}>
                        {editing && editDraft?.ri === rowIndex ? (
                          <div>
                            <textarea
                              value={editDraft.content}
                              onChange={(e) => setEditDraft((d) => ({ ...d, content: e.target.value }))}
                              rows={10}
                              style={{
                                width: "100%", padding: "8px 10px", fontSize: 12.5, lineHeight: 1.55,
                                border: `1.5px solid ${C.primary}`, borderRadius: 8, boxSizing: "border-box",
                                fontFamily: "inherit", resize: "vertical",
                              }}
                            />
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              <button type="button" onClick={saveRowEdit} style={{
                                background: C.primary, color: "#fff", border: "none", borderRadius: 6,
                                padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                              }}>
                                Save to schedule
                              </button>
                              <button type="button" onClick={() => setEditDraft(null)} style={{
                                background: "transparent", color: C.muted, border: `1px solid ${C.border}`,
                                borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer",
                              }}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          parseTaskLines(displayText).map((line, li) => {
                          if (!line.trackable) {
                            return line.stripped
                              ? (
                                <div
                                  key={li}
                                  style={{ whiteSpace: "pre-wrap", margin: "2px 0", cursor: editing ? "pointer" : "default" }}
                                  onClick={() => {
                                    if (!editing) return;
                                    setEditDraft({
                                      ri: rowIndex,
                                      activity: activityTitle,
                                      content: String(contentSource || "").replace(/\\n/g, "\n"),
                                    });
                                  }}
                                >
                                  <RichLineText text={line.text} />
                                </div>
                              )
                              : <div key={li} style={{ height: 4 }} />;
                          }
                          const taskKey  = `${rowIndex}-${line.subIndex}`;
                          const taskDone = subTaskDone.has(taskKey);
                          return (
                            <label key={li} style={{
                              display: "flex", alignItems: "flex-start", gap: 7,
                              padding: "2px 0", cursor: "pointer",
                            }}
                              onClick={(e) => {
                                if (!editing) return;
                                e.preventDefault();
                                setEditDraft({
                                  ri: rowIndex,
                                  activity: activityTitle,
                                  content: String(contentSource || "").replace(/\\n/g, "\n"),
                                });
                              }}
                            >
                              {!editing && (
                              <input
                                type="checkbox"
                                checked={taskDone}
                                onChange={() => handleToggleSubtask(rowIndex, line.subIndex)}
                                title="Mark this specific task done"
                                style={{ width: 14, height: 14, cursor: "pointer", marginTop: 3, flexShrink: 0 }}
                              />
                              )}
                              <RichLineText text={line.text} done={!editing && taskDone} />
                            </label>
                          );
                        })
                        )}
                        <LearningResourceChips youtube={resourced.youtube} youtubeTitle={resourced.youtubeTitle} article={resourced.article} />
                      </div>
                      )}
                    </div>
                  );
                })}
                </div>
              );
            })()}
        </div>
        </>
        )}
      </div>{/* end schedule-scroll */}
      </div>{/* end schedule card */}
    </div>
  );
}
